import React, { useState, useEffect } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useApolloClient, useMutation } from '@apollo/client';
import dynamic, { LoaderComponent } from 'next/dynamic';
import { useIntl, IntlShape } from 'react-intl';
import { useRouter } from 'next/router';
import JobTemplateStyling from '@fuww/library/src/JobTemplateStyling';
import Job from '@fuww/library/src/Job';
import {
  SocialProperties,
  StandardSocialPlatform,
} from '@fuww/library/src/SocialShare/index';
import HiddenLoader from '@fuww/library/src/HiddenLoader';
import overlay from '@fuww/library/src/Palettes/overlay';
import getRelatedJobSearches from '../../../lib/getRelatedJobSearches';
import Head from '../../Head';
import HomeLink from '../../HomeLink';
import messages from '../../../lib/messages.json';
import loading from '../../../lib/loading';
import LinkAndAnchor from '../../LinkAndAnchor';
import { useSiteContext } from '../../SiteContext';
import FormattedDate from '../../FormattedDate';
import shareList from '../../../lib/shareData';
import getJobJsonLdData from '../../../lib/getJobJsonLdData';
import getMostRelevantProfile from '../../../lib/getMostRelevantProfile';
import getDescription from './getDescription';
import { getLocalizedCategory } from '../../../lib/jobCategories';
import ApplyButton from './ApplyButton';
import BackButton from './BackButton';
import getJobMediaUrl from '../../../lib/getJobMediaUrl';
import withQuery from '../../../lib/withQuery';
import query from '../../../lib/queries/jobs/jobQuery.graphql';
import increaseJobPageViews
  from '../../../lib/queries/jobs/increaseJobPageViewsMutation.graphql';
import {
  jobDetailImageConfigurations,
  jobDetailLogoImageConfigurations,
} from '../../../lib/imageConfigurations';
import { JobQuery, JobQueryVariables } from '../../../lib/graphql/api/graphql';
import RelatedNews from './RelatedNews';

const DynamicShareButton = dynamic(() => import(
  '@fuww/library/src/SocialShare/index'
) as LoaderComponent<SocialProperties>, {
  ssr: false,
  loading,
});

const JobTemplate = dynamic(() => import('./JobTemplate'), {
  ssr: true,
  loading,
});
const JobSearchPage = dynamic(() => import('../JobSearch'), {
  ssr: true,
  loading,
});
const JobInfoTable = dynamic(() => import(
  '@fuww/library/src/JobInfoTable'
), {
  ssr: true,
  loading,
});

const JobEbpSpotlight = dynamic(() => import(
  './JobEbpSpotlight'
), {
  ssr: true,
  loading,
});
const JobExpired = dynamic(() => import('@fuww/library/src/JobExpired'), {
  ssr: true,
  loading,
});

const getBreadcrumbs = (
  { id, title, slug }: { id: number | string; title: string; slug: string },
  locale: string | undefined,
  intl: IntlShape,
  isCareerPage: boolean,
  companySlug?: string[] | string,
) => ([
  ...isCareerPage ? [] : [<HomeLink />],
  <LinkAndAnchor
    route={isCareerPage ? `careers.${locale}` : `jobs.${locale}`}
    params={{
      ...isCareerPage ? { companySlug } : {},
    }}
  >
    {intl.formatMessage(messages.fashionJobs)}
  </LinkAndAnchor>,
  <LinkAndAnchor
    route={isCareerPage ? `careers-job.${locale}` : `job.${locale}`}
    params={{
      id: String(id),
      slug,
      ...isCareerPage ? { companySlug } : {},
    }}
  >
    {title}
  </LinkAndAnchor>,
]);

const showApplyButton = (
  active?: boolean | null,
  applyForm?: boolean | null,
  applyUrl?: string | null,
) => (applyForm || applyUrl) && active;

interface JobDetailProperties {
  data?: JobQuery;
  showLoader?: boolean;
}

export const JobDetail = ({
  data,
  showLoader,
}: JobDetailProperties) => {
  const intl = useIntl();
  const showLoaderValue = showLoader ?? false;

  const {
    locale,
    jobsLocale,
    jobsComplete,
    origin,
    isCareerPage,
    hasNews,
  } = useSiteContext();
  const router = useRouter();
  const { query: { companySlug: routerCompanySlug } } = router;
  const [locationHref, setLocationHref] = useState<string>();

  const id = data?.job?.id;

  useEffect(() => {
    setLocationHref(window.location.href);
  }, []);

  const [increasePageViews] = useMutation(increaseJobPageViews);
  const hasTemplate = Boolean(data?.job?.template);
  useEffect(() => {
    if (hasTemplate && typeof id === 'number') {
      increasePageViews({
        variables: {
          id,
        },
      });
    }
  }, [id, hasTemplate, increasePageViews]);

  const apolloClient = useApolloClient();

  if (!showLoaderValue && !data?.job?.template) {
    return (
      <JobSearchPage
        notFound
        data={{}}
        field={null}
        showLoader={showLoaderValue}
      />
    );
  }

  const {
    _id: jobId,
    active,
    applyForm,
    applyUrl,
    title,
    categories,
    buildingName,
    address,
    city,
    postalCode,
    addressRegion,
    country,
    publishedAt,
    company,
    template,
    profiles = [],
  } = { ...data?.job };

  const {
    name: companyName = 'in',
    displayName,
    imageUrls,
    logoUrls,
  } = { ...company };

  const [
    {
      page: { slug: companySlug = '' } = {},
      company: { newsTags = [] } = {},
    } = {},
  ] = getMostRelevantProfile(profiles, companyName);

  const mediaUrl = getJobMediaUrl(imageUrls, logoUrls, origin);

  const filteredShareList = locationHref ? shareList({
    title,
    media: mediaUrl,
    url: locationHref,
    locale,
    fill: 'transparent',
    logoFillColor: overlay[900],
    page: 'job',
  }).filter(
    (share): share is {
      name: StandardSocialPlatform;
      modalProps: {
        title?: string;
        url: string;
        media?: undefined;
      };
      iconProps: {
        bgStyle: { fill: string };
        iconFillColor: string;
        size?: number | undefined;
      };
      additionalProps: { 'aria-label': string };
      className: string;
    } => !share.checkMedia || Boolean(mediaUrl),
  ) : [];

  const jsonLdData = getJobJsonLdData(data?.job, locale, intl);
  const localizedCategories = categories?.map(
    (category) => getLocalizedCategory(intl, category),
  );

  return (
    <>
      <Head
        title={[title, city].filter(Boolean).join(' - ')}
        description={data?.job ? getDescription(data.job, intl) : ''}
        jsonLdData={jsonLdData}
        subjectType="Job"
        subjectId={jobId}
      />

      <Job
        title={title}
        companyName={displayName}
        breadcrumbs={
          data?.job ? getBreadcrumbs(
            data.job,
            locale,
            intl,
            isCareerPage,
            routerCompanySlug,
          ) : []
        }
        fullHeightTitle={active}
        backButton={(
          <BackButton job={data?.job} />
        )}
        shareButtons={(
          <DynamicShareButton
            data={filteredShareList}
          />
        )}
        showLoader={showLoaderValue}
        relatedNews={
          hasNews && (
            newsTags.length > 0
          ) && !showLoaderValue && active && data?.job && (
            <RelatedNews tagSlugs={newsTags} />
          )
        }
      >
        {!showLoaderValue && !active && data?.job && (
          <JobExpired
            archivedJobsHeader={
              intl.formatMessage(messages['archivedJobs.header'])
            }
            moreJobsLink={(
              <LinkAndAnchor
                route={`jobs.${locale}`}
                title={intl.formatMessage(messages['archivedJobs.search'])}
                useRouter
              >
                {intl.formatMessage(messages['archivedJobs.search'])}
              </LinkAndAnchor>
            )}
            relatedSearchesHeader={
              intl.formatMessage(messages['archivedJobs.findSimilar'])
            }
            relatedSearches={getRelatedJobSearches(
              data.job,
              intl,
              locale,
              apolloClient,
            )}
          />
        )}
        <JobTemplateStyling className="job-template">
          {data?.job && company?.name && imageUrls && logoUrls && template
          && (
          <JobTemplate
            job={{
              ...data.job,
              company: {
                ...company,
                imageUrls: imageUrls.slice(0, -1),
                logoUrls: logoUrls.slice(0, -1),
              },
              template,
            }}
            locale={jobsLocale}
          />
          )}
        </JobTemplateStyling>
        {showApplyButton(active, applyForm, applyUrl) && (
          <ApplyButton job={data?.job} />
        )}
        <JobEbpSpotlight job={data?.job} />
        {!showLoaderValue
          && title
          && localizedCategories
          && country
          && city
          && (
          <JobInfoTable
            titleLabel={intl.formatMessage(messages['jobView.JobTitle'])}
            title={title}
            categoryLabel={intl.formatMessage(messages['jobView.category'])}
            category={localizedCategories.join(', ')}
            companyNameLabel={
              intl.formatMessage(messages['jobView.companyName'])
            }
            companyName={
              companySlug
                ? (
                  <LinkAndAnchor
                    route={
                      isCareerPage ? `careers.${locale}` : `jobs.${locale}`
                    }
                    params={{ companySlug }}
                    title={title}
                    useRouter={jobsComplete}
                  >
                    {displayName}
                  </LinkAndAnchor>
                )
                : (
                  <LinkAndAnchor
                    route={`jobs.${locale}`}
                    params={{ keywords: companyName }}
                    title={title}
                    useRouter={jobsComplete}
                  >
                    {displayName}
                  </LinkAndAnchor>
                )
            }
            regionLabel={intl.formatMessage(messages['jobView.regionName'])}
            country={country}
            datePostLabel={intl.formatMessage(messages['jobView.datePost'])}
            datePost={
              publishedAt ? <FormattedDate value={publishedAt} /> : ''
            }
            howToApplyLabel={intl.formatMessage(messages['jobView.howToApply'])}
            howToApply="FashionUnited"
            locationLabel={intl.formatMessage(messages['jobView.location'])}
            buildingName={buildingName}
            address={address}
            city={city}
            addressRegion={addressRegion}
            postalCode={postalCode}
          />
          )}
      </Job>
    </>
  );
};

const JobDetailWithQuery = withQuery<
  JobQuery,
  JobQueryVariables,
  true
>(JobDetail, { renderAlways: true });

const jobDetailWithDataPropertyTypes = {
  id: PropTypes.number.isRequired,
  locale: PropTypes.string.isRequired,
};

export type JobDetailWithDataProperties = InferProps<
typeof jobDetailWithDataPropertyTypes
>;

const JobDetailWithData = ({ id, locale }: JobDetailWithDataProperties) => (
  <JobDetailWithQuery
    query={query}
    variables={{
      id,
      locales: [locale],
      imageConfigurations: jobDetailImageConfigurations,
      logoConfigurations: jobDetailLogoImageConfigurations,
    }}
    errorMessage="Error while loading job."
    loader={<HiddenLoader />}
  />
);

JobDetailWithData.propTypes = jobDetailWithDataPropertyTypes;

export default JobDetailWithData;
